/**
 * PayNowCardV2 - Team Size Selection Card Component
 *
 * WORKFLOW OVERVIEW:
 * ==================
 * 1. INITIALIZATION: Load active plans, resource counts, and billing details
 * 2. USER INTERACTION: Select team size via counter buttons or direct input
 * 3. PROMO CODE: Optional promo code application with validation
 * 4. PRICE CALCULATION: Real-time price updates based on seats + discounts
 * 5. CHECKOUT: Validate and proceed to payment (CREATE) or update (EDIT)
 *
 * COMPONENT FLOW:
 * ===============
 * ┌─────────────────────────────────────────────────────────────┐
 * │  Header Section                                             │
 * │  ├── Title: "Choose your team size"                        │
 * │  └── Manage Link → Navigate to resources                   │
 * ├─────────────────────────────────────────────────────────────┤
 * │  Seats Section                                              │
 * │  ├── Counter: [-] [value] [+]                              │
 * │  └── Price Display: $XXX.XX /month                         │
 * ├─────────────────────────────────────────────────────────────┤
 * │  Warning Banner (conditional)                               │
 * │  └── Minimum seat requirement notice                       │
 * ├─────────────────────────────────────────────────────────────┤
 * │  Promo Section                                              │
 * │  ├── Input Mode: Enter code + Redeem button                │
 * │  └── Applied Mode: Code display + savings + Remove         │
 * ├─────────────────────────────────────────────────────────────┤
 * │  Billing Section                                            │
 * │  ├── Subtotal row                                          │
 * │  ├── Discount row (if applicable)                          │
 * │  └── Total to pay                                          │
 * └─────────────────────────────────────────────────────────────┘
 *
 * API INTEGRATIONS:
 * =================
 * - useGetActivePlan: Fetch available subscription plans
 * - useGetActiveResourceCount: Get current active tech count
 * - useGetMspBillingDetails: Billing address for checkout
 * - useValidatePromoCode: Validate and get discount percentage
 * - useCreateCheckoutSession: Create Stripe checkout session
 * - useUpdateSubscription: Update existing subscription (EDIT mode)
 * - useGetProratedAmount: Calculate prorated charges for seat changes
 */

import { Box, Collapse, Stack, Typography } from "@mui/material";
import GiftBox from "assets/images/gift.png";
import { Button, DialerInput, Input } from "components";
import { ConfirmationBanner } from "components/banner/ConfirmationBanner";
import { ConfirmationModal } from "components/modal/ConfirmationModal";
import {
  useCreateCheckoutSession,
  useGetActivePlan,
  useGetActiveResourceCount,
  useGetMspBillingDetails,
  useGetProratedAmount,
  useGlobalStore,
  useUpdateSubscription,
  useValidatePromoCode,
} from "controller";
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import toast from "react-hot-toast";
import { addMixpanelEvent, debugConsole } from "utilz/helpers";
import { BillingDetailsDrawer } from "../BillingDetailsDrawer";
import { ManageTechDrawer } from "../subscription-details/drawers";

// ============================================================
// CONSTANTS
// ============================================================
const MIN_COUNT_MONTHLY = 2;
const MIN_COUNT_ENTERPRISE = 5;
const MIN_COUNT_ANNUAL = 1;
const DEFAULT_PLAN_AMOUNT = 59;
const TIERED_BASE_PRICE = 499;
const TIERED_EXTRA_SEAT_PRICE = 69;

/** Convert API price (cents, total-for-interval) to monthly dollars */
const toMonthlyDollars = (
  priceCents: number | string,
  interval: "month" | "year",
): number => {
  const dollars = Number(priceCents) / 100;
  return interval === "year" ? dollars / 12 : dollars;
};

/** Calculate tiered monthly price: $499 base + $69 per extra seat */
const calcTieredMonthly = (seatCount: number): number =>
  TIERED_BASE_PRICE +
  Math.max(0, seatCount - MIN_COUNT_ENTERPRISE) * TIERED_EXTRA_SEAT_PRICE;

/** Calculate subtotal based on pricing type */
const calcSubtotal = (
  pricingType: "flat" | "tiered" | undefined,
  seatCount: number,
  planAmount: number,
  billingMultiplier: number,
): number =>
  pricingType === "tiered"
    ? calcTieredMonthly(seatCount) * billingMultiplier
    : seatCount * planAmount * billingMultiplier;

/** Build subtotal label with pricing breakdown */
const buildSubtotalLabel = (
  pricingType: "flat" | "tiered" | undefined,
  seatCount: number,
  planAmount: number,
  billingCycle: "month" | "year",
): { label: string; detail: string } => {
  if (pricingType === "tiered") {
    const extraSeats = seatCount - MIN_COUNT_ENTERPRISE;
    if (extraSeats > 0) {
      return {
        label: "Subtotal",
        detail: `$${TIERED_BASE_PRICE} + (${extraSeats} × $${TIERED_EXTRA_SEAT_PRICE})`,
      };
    }
    return { label: "Subtotal", detail: `$${TIERED_BASE_PRICE}` };
  }
  const formatted = `$${planAmount % 1 === 0 ? planAmount : planAmount.toFixed(2)}`;
  if (billingCycle === "year") {
    return { label: "Subtotal", detail: `(${seatCount} x ${formatted} x 12)` };
  }
  return { label: "Subtotal", detail: `(${seatCount} x ${formatted})` };
};

// ============================================================
// TYPES
// ============================================================
interface PayNowCardV2Props {
  /** Mode: CREATE for new subscription, EDIT for modifying existing */
  type?: "EDIT" | "CREATE";
  /** Existing subscription data (required for EDIT mode) */
  data?: {
    promocode?: string | null;
    seatQuantity: number;
    discountPercentage?: string | null;
    price?: number;
    discountId?: string | null;
    planId: string | null;
    recurringInterval: "month" | "year";
    pricingType?: "flat" | "tiered";
  };
  /** Whether component is in full page view */
  isFullPage?: boolean;
  /** Callback when modal closes (EDIT mode) */
  onClose?: () => void;
  /** Hide internal action button when using external trigger */
  hideActionButton?: boolean;
  /** Billing period for CREATE mode: "monthly" or "annually" */
  billingPeriod?: "monthly" | "annually";
  /** Price per seat for the selected billing period */
  price?: number;
  /** Callback when disabled/loading status changes (for parent reactivity) */
  onStatusChange?: (status: {
    isDisabled: boolean;
    isLoading: boolean;
  }) => void;
}

/** Ref handle for external control of PayNowCardV2 */
export interface PayNowCardV2Ref {
  /** Trigger the proceed/payment action */
  handleProceed: () => Promise<void>;
  /** Whether any async operation is in progress */
  isLoading: boolean;
  /** Whether the action button should be disabled */
  isDisabled: boolean;
  /** The action button text */
  buttonText: string;
}

interface PlanInfo {
  planId: string;
  stripePriceId: string;
}

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Check if billing address exists for checkout
 */
const checkBillingAddressExists = (
  billingDetails: Record<string, unknown> | null,
): boolean => {
  return Boolean(
    billingDetails && billingDetails.line1 && billingDetails.billing_country,
  );
};

/**
 * Generate confirmation modal content based on seat change direction
 */
const getConfirmationContent = (
  currentSeats: number,
  newSeats: number,
  price: number,
): string => {
  if (currentSeats > newSeats) {
    return `Your seat count has been adjusted from ${currentSeats} to ${newSeats}. Click Update to confirm.`;
  }
  return `Your seat count has been adjusted from ${currentSeats} to ${newSeats}. You will now be charged $${price.toFixed(2)}. Click 'Pay Now' to confirm.`;
};

// ============================================================
// SUB-COMPONENTS
// ============================================================

/**
 * Header Section - Title and Manage link
 * WORKFLOW: Displays card title with navigation to resource management
 */
interface HeaderSectionProps {
  onManageClick: () => void;
  type: "EDIT" | "CREATE";
}

// Static styles for HeaderSection - extracted to avoid recreation on every render
const headerContainerStyles = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};
const titleRowStyles = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
};
const manageButtonStaticStyles = {
  minWidth: "0px",
  fontSize: "14px",
  fontWeight: 600,
};

const HeaderSection = memo<HeaderSectionProps>(({ onManageClick, type }) => {
  return (
    <Box sx={headerContainerStyles}>
      {/* Title Row */}
      <Box sx={titleRowStyles}>
        <Typography
          variant="h6"
          sx={(theme) => ({ color: theme.palette.lightBg.high })}
        >
          Choose your team size
        </Typography>
        {type === "CREATE" && (
          <Button
            variant="ghost"
            onClick={onManageClick}
            sx={(theme) => ({
              ...manageButtonStaticStyles,
              color: theme.palette.primary.main,
            })}
          >
            Manage
          </Button>
        )}
      </Box>
      {/* Subtitle */}
      <Typography
        variant="body-small"
        sx={(theme) => ({ color: theme.palette.lightBg.low })}
      >
        You can add or remove seats anytime as your team grows or changes.
      </Typography>
    </Box>
  );
});

HeaderSection.displayName = "HeaderSection";

/**
 * Seats Counter Section - Counter input with price display
 * WORKFLOW: User adjusts seat count → triggers price recalculation
 */
interface SeatsSectionProps {
  seatCount: number;
  minSeats: number;
  priceDisplay: string;
  onValueChange: (value: string) => void;
}

// Static styles for SeatsSection - extracted to avoid recreation on every render
const seatsContainerStaticStyles = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderRadius: "6px",
};

const counterContainerStyles = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const priceDisplayStyles = {
  display: "flex",
  alignItems: "baseline",
  gap: "4px",
};

const SeatsSection = memo<SeatsSectionProps>(
  ({ seatCount, minSeats, priceDisplay, onValueChange }) => {
    return (
      <Box
        sx={(theme) => ({
          ...seatsContainerStaticStyles,
          backgroundColor: theme.palette.neutral["005"],
        })}
      >
        {/* Counter Input */}
        <Box sx={counterContainerStyles}>
          <DialerInput
            value={seatCount.toString()}
            onValueChange={onValueChange}
            minValue={minSeats}
          />
          <Typography
            variant="body-small"
            sx={(theme) => ({ color: theme.palette.lightBg.medium, ml: 1 })}
          >
            Seats
          </Typography>
        </Box>

        {/* Price Display */}
        <Box sx={priceDisplayStyles}>
          <Typography
            variant="h5"
            sx={(theme) => ({
              color: theme.palette.lightBg.high,
              fontWeight: 700,
            })}
          >
            {priceDisplay}
          </Typography>
          <Typography
            variant="body-small"
            sx={(theme) => ({ color: theme.palette.lightBg.medium })}
          >
            /month
          </Typography>
        </Box>
      </Box>
    );
  },
);

SeatsSection.displayName = "SeatsSection";

/**
 * Promo Code Section - Input or Applied state display
 * WORKFLOW:
 * 1. Input Mode: User enters code → validates via API → applies discount
 * 2. Applied Mode: Shows applied code + savings → can remove
 */
interface PromoSectionProps {
  appliedCode: string | null;
  totalSavings: number;
  errorMessage: string | null;
  isValidating: boolean;
  onRedeem: (code: string) => void;
  onRemove: () => void;
}

// Static styles for PromoSection - extracted to avoid recreation on every render
const promoContainerStyles = { marginTop: "24px" };

const promoLabelStaticStyles = {
  marginBottom: "8px",
  display: "block",
};

const appliedPromoCardStaticStyles = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  borderRadius: "6px",
  overflow: "hidden",
};

const giftIconBoxStaticStyles = {
  padding: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const promoTextBoxStyles = { flex: 1 };

const removeButtonStaticStyles = {
  fontSize: "12px",
  "&:hover": { backgroundColor: "transparent" },
};

const promoInputContainerStyles = {
  display: "flex",
  gap: "12px",
  alignItems: "flex-start",
};

const promoInputStaticStyles = {
  flex: 1,
};

const PromoSection = memo<PromoSectionProps>(
  ({
    appliedCode,
    totalSavings,
    errorMessage,
    isValidating,
    onRedeem,
    onRemove,
  }) => {
    const [inputValue, setInputValue] = useState("");

    const handleRedeem = () => {
      if (inputValue.trim()) {
        onRedeem(inputValue.trim().toUpperCase());
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value.toUpperCase());
    };

    return (
      <Box sx={promoContainerStyles}>
        {/* Label */}
        <Typography
          variant="caption-2"
          sx={(theme) => ({
            ...promoLabelStaticStyles,
            color: theme.palette.lightBg.high,
          })}
        >
          Promo code
        </Typography>

        {appliedCode ? (
          /* Applied Promo Card */
          <Box
            sx={(theme) => ({
              ...appliedPromoCardStaticStyles,
              border: `1px solid ${theme.palette.primitive.system.red[50]}`,
            })}
          >
            {/* Gift Icon */}
            <Box
              sx={(theme) => ({
                ...giftIconBoxStaticStyles,
                backgroundColor: theme.palette.primitive.system.red[50],
              })}
            >
              <img
                src={GiftBox}
                alt="gift_box"
                width={"47px"}
                height={"47px"}
              />
            </Box>
            {/* Promo Text */}
            <Box sx={promoTextBoxStyles}>
              <Typography
                variant="body-medium"
                sx={(theme) => ({ color: theme.palette.lightBg.high })}
              >
                {appliedCode} applied
              </Typography>
              <Typography
                variant="body-small"
                sx={(theme) => ({ color: theme.palette.lightBg.medium })}
              >
                ${totalSavings.toFixed(2)} promo savings
              </Typography>
            </Box>
            {/* Remove Button */}
            <Button
              variant="ghost"
              onClick={onRemove}
              sx={(theme) => ({
                ...removeButtonStaticStyles,
                color: theme.palette.error.main,
              })}
            >
              Remove
            </Button>
          </Box>
        ) : (
          /* Promo Input */
          <Box sx={promoInputContainerStyles}>
            <Input
              fullWidth
              name="promoCode"
              placeholder="Enter promo code"
              value={inputValue}
              onChange={handleInputChange}
              error={Boolean(errorMessage)}
              errorMessage={errorMessage || ""}
              hideHelperBox={!errorMessage}
              sx={(theme) => ({
                ...promoInputStaticStyles,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.palette.neutral.main,
                },
              })}
            />
            <Button
              variant="secondary"
              onClick={handleRedeem}
              loading={isValidating}
              disabled={!inputValue.trim()}
            >
              Redeem
            </Button>
          </Box>
        )}
      </Box>
    );
  },
);

PromoSection.displayName = "PromoSection";

/**
 * Billing Section - Price breakdown and totals
 * WORKFLOW: Displays subtotal, discounts, and final amount to pay
 */
interface BillingSectionProps {
  subtotalLabel: { label: string; detail: string };
  subtotal: number;
  discount: number;
  total: number;
}

// Static styles for BillingSection - extracted to avoid recreation on every render
const billingContainerStyles = {
  marginTop: "24px",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const billingRowStyles = {
  display: "flex",
  justifyContent: "space-between",
  fontSize: "14px",
};

const dividerStaticStyles = {
  marginTop: "4px",
  marginBottom: "4px",
};

const totalRowStyles = {
  display: "flex",
  justifyContent: "space-between",
  marginTop: "8px",
};

const totalTypographyStaticStyles = {
  fontWeight: 700,
};

const BillingSection = memo<BillingSectionProps>(
  ({ subtotalLabel, subtotal, discount, total }) => {
    return (
      <Box sx={billingContainerStyles}>
        {/* Header */}
        <Typography
          variant="h6"
          sx={(theme) => ({ color: theme.palette.lightBg.high })}
        >
          Billing details
        </Typography>

        {/* Subtotal Row */}
        <Box sx={billingRowStyles}>
          <Typography
            variant="body-medium"
            sx={(theme) => ({ color: theme.palette.lightBg.medium })}
          >
            {subtotalLabel.label}{" "}
            <Typography
              component="span"
              variant="body-medium"
              sx={(theme) => ({ color: theme.palette.lightBg.low })}
            >
              {subtotalLabel.detail}
            </Typography>
          </Typography>
          <Typography
            variant="h6"
            sx={(theme) => ({ color: theme.palette.lightBg.high })}
          >
            ${subtotal.toFixed(2)}
          </Typography>
        </Box>

        {/* Discount Row (conditional) */}
        {discount > 0 && (
          <Box sx={billingRowStyles}>
            <Typography
              variant="body-medium"
              sx={(theme) => ({ color: theme.palette.lightBg.medium })}
            >
              Discount
            </Typography>
            <Typography
              variant="h6"
              sx={(theme) => ({ color: theme.palette.lightBg.high })}
            >
              -${discount.toFixed(2)}
            </Typography>
          </Box>
        )}

        {/* Divider */}
        <Box
          sx={(theme) => ({
            ...dividerStaticStyles,
            borderTop: `1px dashed ${theme.palette.neutral["015"]}`,
          })}
        />

        {/* Total Row */}
        <Box sx={totalRowStyles}>
          <Typography
            variant="h5"
            sx={(theme) => ({
              ...totalTypographyStaticStyles,
              color: theme.palette.primary.main,
            })}
          >
            To pay
          </Typography>
          <Typography
            variant="h4"
            sx={(theme) => ({
              ...totalTypographyStaticStyles,
              color: theme.palette.primary.main,
            })}
          >
            ${total.toFixed(2)}
          </Typography>
        </Box>
      </Box>
    );
  },
);

BillingSection.displayName = "BillingSection";

// ============================================================
// MAIN COMPONENT
// ============================================================

/**
 * PayNowCardV2 - Main Component
 *
 * COMPLETE WORKFLOW:
 * ==================
 * 1. MOUNT: Fetch active plans, resource counts, billing details
 * 2. INITIALIZE: Set seat count based on active resources or minimum
 * 3. EDIT MODE: Pre-populate with existing subscription data
 * 4. USER ACTIONS:
 *    - Adjust seats → recalculate price
 *    - Apply promo → validate + apply discount
 *    - Click Proceed → validate → checkout flow
 * 5. CHECKOUT:
 *    - CREATE: Check billing → create session → redirect to Stripe
 *    - EDIT: Confirm → update subscription → reload
 */
const PayNowCardV2Inner = forwardRef<PayNowCardV2Ref, PayNowCardV2Props>(
  (
    {
      type = "CREATE",
      data,
      isFullPage = false,
      hideActionButton = false,
      billingPeriod,
      price,
      onStatusChange,
    },
    ref,
  ) => {
    // ============================================================
    // GLOBAL STATE
    // ============================================================
    const mspId = useGlobalStore((state) => state.mspId);

    // ============================================================
    // LOCAL STATE
    // ============================================================
    // Seat count initialized to plan minimum; useEffect will adjust once activeResourceCount loads
    const [seatCount, setSeatCount] = useState(
      billingPeriod === "annually" ? MIN_COUNT_ANNUAL : MIN_COUNT_MONTHLY,
    );
    const [billingCycle, setBillingCycle] = useState<"month" | "year">(
      billingPeriod === "annually" ? "year" : "month",
    );
    // Initialize plan amount from props if available, otherwise use default
    const [planAmount, setPlanAmount] = useState(price ?? DEFAULT_PLAN_AMOUNT);
    const [planInfo, setPlanInfo] = useState<PlanInfo | null>(null);
    const [promoCode, setPromoCode] = useState<string | null>(null);
    const [discountPercentage, setDiscountPercentage] = useState(0);
    const [promoError, setPromoError] = useState<string | null>(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showBillingDrawer, setShowBillingDrawer] = useState<{
      open: boolean;
      type: "CREATE" | "EDIT";
    }>({ open: false, type: "EDIT" });
    const [isManageTechDrawerOpen, setIsManageTechDrawerOpen] = useState(false);

    // ============================================================
    // API HOOKS
    // ============================================================
    const { data: activePlans } = useGetActivePlan();
    const { data: activeResourceCount } = useGetActiveResourceCount();
    const { data: mspBillingDetails } = useGetMspBillingDetails(mspId || "");

    const { mutateAsync: validateCode, isLoading: isValidatingPromo } =
      useValidatePromoCode();
    const {
      mutateAsync: createSubscription,
      isLoading: isCreatingSubscription,
    } = useCreateCheckoutSession();
    const {
      mutateAsync: updateSubscription,
      isLoading: isUpdatingSubscription,
    } = useUpdateSubscription();

    // Prorated amount for EDIT mode - calculates cost for seat changes mid-billing cycle
    const { data: proratedAmount } = useGetProratedAmount({
      enabled: type === "EDIT",
      promoCode: promoCode || undefined,
      ...(promoCode === data?.promocode
        ? { discountId: data?.discountId || undefined }
        : {}),
      ...(data?.pricingType === "tiered" ? { quantity: seatCount } : {}),
    });

    // ============================================================
    // COMPUTED VALUES
    // ============================================================
    /**
     * Minimum seats based on plan type and billing cycle:
     * - Enterprise (tiered): MIN_COUNT_ENTERPRISE (5)
     * - Monthly: MIN_COUNT_MONTHLY (2)
     * - Annual: MIN_COUNT_ANNUAL (1)
     */
    const minSeats =
      data?.pricingType === "tiered"
        ? MIN_COUNT_ENTERPRISE
        : billingCycle === "year"
          ? MIN_COUNT_ANNUAL
          : MIN_COUNT_MONTHLY;

    const resourceCount = Number(activeResourceCount) || 0;
    const effectiveMinSeats = Math.max(resourceCount, minSeats);

    const bannerDescription =
      seatCount === resourceCount && resourceCount > 0
        ? `You already have ${resourceCount} active technicians`
        : `Minimum ${minSeats} seats required`;

    const billingMultiplier = billingCycle === "year" ? 12 : 1;
    const subtotal = calcSubtotal(
      data?.pricingType,
      seatCount,
      planAmount,
      billingMultiplier,
    );
    const discountAmount =
      discountPercentage > 0 ? subtotal * (discountPercentage / 100) : 0;
    const totalPrice = subtotal - discountAmount;

    const subtotalLabel = useMemo(
      () =>
        buildSubtotalLabel(
          data?.pricingType,
          seatCount,
          planAmount,
          billingCycle,
        ),
      [seatCount, planAmount, billingCycle, data?.pricingType],
    );

    /**
     * Prorated price for EDIT mode
     * When changing seats mid-billing cycle, calculate the prorated charge
     * for additional seats or show 0 for seat reductions
     */
    const proratedPrice = useMemo(() => {
      if (type !== "EDIT" || !data?.seatQuantity) {
        return totalPrice;
      }

      const seatDifference = seatCount - data.seatQuantity;

      // If reducing seats, no charge (handled as subscription update)
      if (seatDifference <= 0) {
        return 0;
      }

      // Use prorated amount from API if available
      if (proratedAmount?.amount) {
        // if enterprice plan / tiered then the calculation is different
        const proratedPerSeat =
          data.pricingType === "tiered"
            ? proratedAmount.amount / seatDifference / 100
            : proratedAmount.amount / 10000;
        const proratedTotal = seatDifference * proratedPerSeat;
        // Apply discount if applicable
        return discountPercentage > 0
          ? proratedTotal * ((100 - discountPercentage) / 100)
          : proratedTotal;
      }

      // Fallback: calculate based on plan amount with discount
      const seatPrice = planAmount * billingMultiplier;
      return discountPercentage > 0
        ? seatDifference * seatPrice * ((100 - discountPercentage) / 100)
        : seatDifference * seatPrice;
    }, [
      type,
      data?.seatQuantity,
      seatCount,
      proratedAmount?.amount,
      discountPercentage,
      planAmount,
      billingMultiplier,
      totalPrice,
    ]);

    // Price to display - use prorated price in EDIT mode, total price in CREATE mode
    const displayPrice = type === "EDIT" ? proratedPrice : totalPrice;
    const monthlyPrice = useMemo(() => {
      const monthly =
        data?.pricingType === "tiered"
          ? calcTieredMonthly(seatCount)
          : seatCount * planAmount;
      return discountPercentage > 0
        ? monthly * ((100 - discountPercentage) / 100)
        : monthly;
    }, [seatCount, planAmount, discountPercentage, data?.pricingType]);

    const priceDisplay = `$${monthlyPrice.toFixed(2)}`;

    const confirmationContent = getConfirmationContent(
      data?.seatQuantity || 1,
      seatCount,
      proratedPrice,
    );

    const confirmationButtonText =
      data?.seatQuantity && seatCount < data.seatQuantity
        ? "Update seat count"
        : "Pay now";

    const actionButtonText =
      type === "EDIT" ? "Update subscription" : "Proceed to payment";

    const isActionButtonDisabled =
      type === "EDIT" && seatCount === data?.seatQuantity;

    // ============================================================
    // EFFECTS
    // ============================================================

    /**
     * WORKFLOW: Update billing cycle when billingPeriod prop changes (CREATE mode)
     */
    useEffect(() => {
      if (type === "CREATE" && billingPeriod) {
        setBillingCycle(billingPeriod === "annually" ? "year" : "month");
      }
    }, [billingPeriod, type]);

    /**
     * WORKFLOW: Update plan amount when price prop changes (CREATE mode)
     */
    useEffect(() => {
      if (type === "CREATE" && price !== undefined) {
        setPlanAmount(price);
      }
    }, [price, type]);

    /**
     * WORKFLOW: Set seat count based on active resources and minimum seats
     * Runs when activeResourceCount loads or minSeats changes (e.g., tiered pricing)
     */
    useEffect(() => {
      if (type !== "EDIT") {
        const resourceCount = Number(activeResourceCount) || 0;
        setSeatCount((prev) => Math.max(prev, resourceCount, minSeats));
      }
    }, [activeResourceCount, minSeats, type]);

    /**
     * WORKFLOW: Populate form with existing subscription data (EDIT mode)
     */
    useEffect(() => {
      if (data && type === "EDIT") {
        setSeatCount(data.seatQuantity);
        setPromoCode(data.promocode || null);
        setDiscountPercentage(Number(data.discountPercentage) || 0);
        setPlanAmount(
          data.price
            ? toMonthlyDollars(data.price, data.recurringInterval)
            : DEFAULT_PLAN_AMOUNT,
        );
        setPlanInfo({
          planId: data.planId || "",
          stripePriceId: "",
        });
        setBillingCycle(data.recurringInterval || "month");
      }
    }, [data, type]);

    /**
     * WORKFLOW: Update plan info when active plans load
     * In CREATE mode, prefer price prop if provided
     */
    useEffect(() => {
      if (activePlans && type !== "EDIT" && activePlans.length > 0) {
        const plan = activePlans.find(
          (item: Record<string, unknown>) =>
            item.recurring_interval === billingCycle,
        );
        if (plan) {
          // Use props price if provided (CREATE mode), otherwise fall back to API price
          if (price !== undefined) {
            setPlanAmount(price);
          } else {
            setPlanAmount(
              toMonthlyDollars(plan.price, plan.recurring_interval),
            );
          }
          setPlanInfo({
            planId: plan.mapping_id,
            stripePriceId: plan.stripe_price_id,
          });
        }
      }
    }, [activePlans, billingCycle, type, price]);

    // ============================================================
    // HANDLERS (Memoized Callbacks)
    // ============================================================

    /**
     * WORKFLOW: Handle seat count value change from DialerInput
     */
    const handleSeatValueChange = useCallback((value: string) => {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue)) {
        setSeatCount(numValue);
      }
    }, []);

    /**
     * WORKFLOW: Open ManageTechDrawer to manage technicians
     */
    const handleManageClick = useCallback(() => {
      setIsManageTechDrawerOpen(true);
    }, []);

    /**
     * WORKFLOW: Validate and apply promo code
     * 1. Call API to validate code
     * 2. On success: Set promo code + discount percentage
     * 3. On failure: Show error message
     */
    const handlePromoRedeem = useCallback(
      async (code: string) => {
        setPromoError(null);
        try {
          const result = await validateCode({
            code,
            planId: planInfo?.planId || "",
          });

          if (result?.success) {
            setPromoCode(code);
            setDiscountPercentage(result.data?.percent_off || 0);
          } else {
            setPromoError("Invalid promo code");
          }
        } catch (error) {
          setPromoError("Unable to validate promo code");
          debugConsole(error);
        }
      },
      [planInfo, validateCode],
    );

    /**
     * WORKFLOW: Remove applied promo code
     */
    const handlePromoRemove = useCallback(() => {
      setPromoCode(null);
      setDiscountPercentage(0);
      setPromoError(null);
    }, []);

    /**
     * WORKFLOW: Create Stripe checkout session
     * 1. Call API with subscription details
     * 2. Redirect to Stripe checkout URL
     */
    const createCheckoutSession = useCallback(async () => {
      try {
        const checkoutUrl = await createSubscription({
          promocode: promoCode,
          seatQuantity: seatCount,
          planId: planInfo?.planId || "",
          stripePriceId: planInfo?.stripePriceId || "",
        });
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
        }
      } catch (error) {
        toast.error("Unable to create subscription.");
        setShowBillingDrawer({ open: false, type: "EDIT" });
        debugConsole(error);
      }
    }, [createSubscription, promoCode, seatCount, planInfo]);

    /**
     * WORKFLOW: Update existing subscription (EDIT mode)
     */
    const handleUpdateSubscription = useCallback(async () => {
      try {
        await updateSubscription({
          seatQuantity: seatCount,
          ...(promoCode !== data?.promocode && { promocode: promoCode }),
          planId: planInfo?.planId || "",
          stripePriceId: planInfo?.stripePriceId || "",
        });
        setShowConfirmModal(false);
        window.location.reload();
      } catch (error) {
        debugConsole(error);
        toast.error("Unable to update subscription.");
      }
    }, [updateSubscription, seatCount, promoCode, data?.promocode, planInfo]);

    /**
     * WORKFLOW: Main proceed/pay button click handler
     * 1. Validate seat count >= active resources
     * 2. Validate seat count >= minimum
     * 3. EDIT mode: Show confirmation modal
     * 4. CREATE mode: Check billing → create checkout session
     */
    const handleProceed = useCallback(async () => {
      // Validation
      if (seatCount < (activeResourceCount || 0)) {
        toast.error("Seat count cannot be less than active techs.");
        return;
      }

      if (seatCount < minSeats) {
        toast.error(`Minimum ${minSeats} seats required.`);
        return;
      }

      if (type === "EDIT") {
        setShowConfirmModal(true);
      } else {
        if (checkBillingAddressExists(mspBillingDetails)) {
          addMixpanelEvent({ event: "subscription_payment_initiated" });
          await createCheckoutSession();
        } else {
          setShowBillingDrawer({ open: true, type: "CREATE" });
        }
      }
    }, [
      seatCount,
      activeResourceCount,
      minSeats,
      type,
      mspBillingDetails,
      createCheckoutSession,
    ]);

    /**
     * WORKFLOW: Handle billing drawer close
     */
    const handleBillingDrawerClose = () => {
      setShowBillingDrawer((prev) => ({ ...prev, open: false }));
    };

    /**
     * WORKFLOW: Billing details saved successfully → proceed to checkout
     */
    const handleBillingSuccess = () => {
      addMixpanelEvent({ event: "subscription_payment_initiated" });
      createCheckoutSession();
    };

    // Notify parent of status changes for reactive updates
    useEffect(() => {
      onStatusChange?.({
        isDisabled: isActionButtonDisabled,
        isLoading: isCreatingSubscription || isUpdatingSubscription,
      });
    }, [
      isActionButtonDisabled,
      isCreatingSubscription,
      isUpdatingSubscription,
      onStatusChange,
    ]);

    // ============================================================
    // IMPERATIVE HANDLE - Expose methods to parent via ref
    // ============================================================
    useImperativeHandle(
      ref,
      () => ({
        handleProceed,
        isLoading: isCreatingSubscription || isUpdatingSubscription,
        isDisabled: isActionButtonDisabled,
        buttonText: actionButtonText,
      }),
      [
        handleProceed,
        isCreatingSubscription,
        isUpdatingSubscription,
        isActionButtonDisabled,
        actionButtonText,
      ],
    );

    // ============================================================
    // RENDER
    // ============================================================
    return (
      <Stack
        sx={(theme) => ({
          height: "100%",
          padding: "16px",
          borderRadius: "8px",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          border: `1px solid ${theme.palette.neutral["015"]}`,
          gap: "8px",
          backgroundColor: theme.palette.neutral.main,
        })}
      >
        <Stack
          sx={(theme) => ({
            backgroundColor: theme.palette.neutral["005"],
            p: "16px",
            borderRadius: "8px",
            gap: "16px",
          })}
        >
          {/* Header Section */}
          <HeaderSection onManageClick={handleManageClick} type={type} />

          {/* Seats Counter Section */}
          <SeatsSection
            seatCount={seatCount}
            minSeats={effectiveMinSeats}
            priceDisplay={priceDisplay}
            onValueChange={handleSeatValueChange}
          />
        </Stack>

        {/* Warning Banner - Show minimum seat requirement */}
        <Collapse in={effectiveMinSeats === seatCount} timeout={300}>
          <ConfirmationBanner
            description={bannerDescription}
            onConfirm={() => {}}
            onCancel={() => {}}
            containerStyle={{ "& > :last-child": { display: "none" } }}
          />
        </Collapse>

        {/* Promo Code Section */}
        <PromoSection
          appliedCode={promoCode}
          totalSavings={discountAmount}
          errorMessage={promoError}
          isValidating={isValidatingPromo}
          onRedeem={handlePromoRedeem}
          onRemove={handlePromoRemove}
        />

        {/* Billing Details Section */}
        <BillingSection
          subtotalLabel={subtotalLabel}
          subtotal={subtotal}
          discount={discountAmount}
          total={displayPrice}
        />

        {/* Action Button - Hidden when parent controls the action */}
        {!hideActionButton && (
          <Box sx={{ marginTop: "24px" }}>
            <Button
              variant="primary"
              fullWidth
              onClick={handleProceed}
              loading={isCreatingSubscription}
              disabled={isActionButtonDisabled}
            >
              {actionButtonText}
            </Button>
          </Box>
        )}

        {/* Billing Details Drawer */}
        {showBillingDrawer.open && (
          <BillingDetailsDrawer
            open={showBillingDrawer.open}
            onClose={handleBillingDrawerClose}
            mspBillingDetails={mspBillingDetails}
            type={showBillingDrawer.type}
            successCallback={handleBillingSuccess}
          />
        )}

        {/* Manage Technicians Drawer */}
        <ManageTechDrawer
          open={isManageTechDrawerOpen}
          onClose={() => setIsManageTechDrawerOpen(false)}
        />

        {/* Confirmation Modal (EDIT mode) */}
        {showConfirmModal && (
          <ConfirmationModal
            open={showConfirmModal}
            showIcon={false}
            isLoading={isUpdatingSubscription}
            type="primary"
            primaryButtonText={confirmationButtonText}
            onClose={() => setShowConfirmModal(false)}
            onSecondaryButtonClick={() => setShowConfirmModal(false)}
            onPrimaryButtonClick={handleUpdateSubscription}
            title="Confirm seat change"
            tagline={confirmationContent}
          />
        )}
      </Stack>
    );
  },
);

PayNowCardV2Inner.displayName = "PayNowCardV2";

export const PayNowCardV2 = memo(PayNowCardV2Inner);

export default PayNowCardV2;
